import Controller from '@ember/controller';
import { capitalize } from '@ember/string';
import { inject as service } from '@ember/service';
import { isEmpty, isPresent } from '@ember/utils';
import { action, computed } from '@ember/object';
import { task, timeout } from 'ember-concurrency';
import config from 'cio/config/environment';
import moment from 'moment-timezone';

import pluralize from 'cio/utils/pluralize';
import ignorePromiseFailure from 'cio/utils/ignore-promise-failure';
import actionInfo from 'cio/utils/action-info';

const FILTER_QUERY_PARAM_DEFAULTS = {
  campaign: null,
  action: null,
  recipient: null,
};

function serializeDeliveriesFilters({
  deliveryStatus,
  campaign,
  action,
  actionType,
  drafted,
  startTs,
  endTs,
  broadcastTrigger,
  exportStartTs,
  exportEndTs,
}) {
  let data = {
    start_ts: 0,
    end_ts: 0,
    metric: deliveryStatus ?? '',
    delivery_type: actionType ?? '',
  };

  if (exportEndTs != null && exportStartTs != null) {
    data.start_ts = parseInt(exportStartTs);
    data.end_ts = parseInt(exportEndTs);
  } else {
    if (startTs != null) {
      data.start_ts = parseInt(startTs);
    }
    if (endTs != null) {
      data.end_ts = parseInt(endTs);
    }
  }

  if (/^newsletter_/.test(campaign)) {
    data.newsletter_id = campaign.replace(/newsletter_/, '');
  } else if (/^template_/.test(campaign)) {
    data.template_id = campaign.replace('template_', '');
  } else if (/^transactional_/.test(campaign)) {
    data.transactional_message_id = campaign.replace('transactional_', '');
  } else if (isPresent(campaign)) {
    data.campaign_id = campaign;
  }

  data.action_id = isPresent(action) ? action : null;

  if (drafted) {
    data.drafted = drafted;
    delete data.metric;
  }

  if (broadcastTrigger) {
    // Broadcast trigger IDs are of the format campaignId-triggerId, e.g. 1-3,
    // and we need to extract the trigger ID before sending it.
    let [, trigger] = broadcastTrigger.split('-');
    data.trigger_id = parseInt(trigger);
  }

  return data;
}

export default class DeliveriesController extends Controller.extend({
  personId: null,
}) {
  @service alerts;
  @service api;
  @service backfills;
  @service store;

  queryParams = [];
  filterQueryParams = [];

  campaign = null;
  continuation = null;
  broadcastTrigger = null;
  deliveries = [];
  fetchDrafted = false;
  action = null;
  startTs = null;
  exportStartTs = null;
  endTs = null;
  exportEndTs = null;
  recipient = null;
  deliveryStatus = null;

  actionType = null;
  drafted = false;

  getDeliveryQuery(continuation = 0) {
    let { campaign, recipient, action, broadcastTrigger: trigger } = this;

    let query = {
      continuation,
      recipient,
      internal_id: this.personId,
      drafts: this.fetchDrafted,
      metric: this.deliveryStatus,
      start_ts: this.startTs ?? 0,
      end_ts: this.endTs ?? 0,
      type: this.actionType ?? '',
    };

    if (!action) {
      if (trigger) {
        query.trigger_id = trigger;
      }
      if (/^newsletter_/.test(campaign)) {
        query.newsletter_id = campaign.replace('newsletter_', '');
      } else if (/^template_/.test(campaign)) {
        query.template_id = campaign.replace('template_', '');
      } else if (/^transactional_/.test(campaign)) {
        query.transactional_message_id = campaign.replace('transactional_', '');
      } else {
        query.campaign_id = campaign;
      }
    } else {
      query.action_id = action;
    }

    for (let param in query) {
      if (isEmpty(query[param])) {
        delete query[param];
      }
    }

    return query;
  }
  queryDeliveries(query) {
    return this.store.query('delivery', query);
  }
  resetPagination() {
    this.fetchDeliveries.perform();
  }

  @computed('actionType')
  get actionInfo() {
    return actionInfo(`${this.actionType}_action`);
  }

  fetchDeliveries = task({ restartable: true }, async () => {
    this.set('continuation', '');

    await timeout(config.APP.debounceDuration);

    let results = await this.queryDeliveries(this.getDeliveryQuery());

    this.setProperties({
      deliveries: results.toArray(),
      continuation: results.get('meta.continuation'),
      lastUpdated: moment().unix(),
    });

    return results;
  });

  fetchMore = task({ drop: true }, async () => {
    let response = await this.queryDeliveries(this.getDeliveryQuery(this.continuation));

    this.deliveries.pushObjects(response.toArray());

    this.set('continuation', response.get('meta.continuation'));
  });

  // eslint-disable-next-line @typescript-eslint/naming-convention
  _markSelectedDeliveries(delivery_ids, pendingFieldName) {
    let { deliveries } = this;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    if (delivery_ids) {
      deliveries = deliveries.filter((delivery) => delivery_ids.includes(delivery.get('id')));
    }

    deliveries.filterBy('canBeSentOrDeleted').setEach(pendingFieldName, true);
  }

  _normalizeData(data) {
    // If specific delivery ids are provided, ignore
    // the campaign or template settings as any actions
    // should only affect the specific deliveries selected
    if (data.delivery_ids) {
      delete data.campaign_id;
      delete data.newsletter_id;
      delete data.action_id;
      delete data.template_id;
      delete data.transactional_message_id;
    }

    if (data.itemLabel === 'draft') {
      data.draft = true;
    }

    // For some messages, the "campaign" is set to "template_{id}" or "transactional_{id}" and
    // we want to send template_id: <id> or transactional_message_id: <id>
    if (data.campaign_id?.startsWith('template_')) {
      data.template_id = data.campaign_id.replace('template_', '');
      delete data.campaign_id;
    } else if (data.campaign_id?.startsWith('transactional_')) {
      data.transactional_message_id = data.campaign_id.replace('transactional_', '');
      delete data.campaign_id;
    }

    data.type = this.get('actionType');

    if (isEmpty(data.type)) {
      data.type = 'all';
    }

    return data;
  }

  _complete(verb, data) {
    let verbMap = {
      send: {
        doing: 'sending',
        done: 'sent',
        endpoint: 'send',
        httpVerb: 'post',
      },
      retry: {
        doing: 'retrying',
        done: 'retried',
        endpoint: 'retry',
        httpVerb: 'post',
      },
      redeliver: {
        doing: 'sending again',
        done: 'sent again',
        endpoint: 'redeliver',
        httpVerb: 'post',
      },

      delete: {
        doing: 'deleting',
        done: 'deleted',
        endpoint: 'destroy',
        httpVerb: 'delete',
      },
    };

    let { doing, done, endpoint, httpVerb } = verbMap[verb];

    data = this._normalizeData(data);

    let { delivery_ids, itemLabel } = data;
    let label = pluralize(2, itemLabel);
    // eslint-disable-next-line @typescript-eslint/naming-convention
    if (delivery_ids) {
      label = pluralize(delivery_ids.length, itemLabel);
    }

    this.get('alerts').sticky(`delivery.${verb}`, `${capitalize(doing)} your ${label}…`);

    return this.get('api')
      [httpVerb](`deliveries/${endpoint}`, { data })
      .then(
        () => {
          this.get('alerts').success(`delivery.${verb}`, `Your ${label} will be ${done} soon`);

          this._markSelectedDeliveries(delivery_ids, `pending${capitalize(verb)}`);
          this.get('backfills.fetch').perform();
        },
        () => this.get('alerts').remove(`delivery.${verb}`),
      );
  }

  @action
  clearFilters() {
    this.filterQueryParams.forEach((param) => this.set(param, FILTER_QUERY_PARAM_DEFAULTS[param]));
  }

  @action
  update() {
    this.fetchDeliveries.perform();
  }

  @action
  export(from) {
    let filters = this.getProperties(
      'campaign',
      'action',
      'deliveryStatus',
      'actionType',
      'drafted',
      'startTs',
      'endTs',
      'broadcastTrigger',
      'exportStartTs',
      'exportEndTs',
    );

    let data = serializeDeliveriesFilters(filters);

    // This includees the recipients email in the export
    data.attributes = ['email'];

    // This export action can be called from several different places in the app. When an export
    // is created, the backend sends an event to our analytics services. The templates pass a
    // unique identifier that is sent to the backend.
    if (from) {
      data.exported_from = from;
    }

    let promise = this.get('api').post('exports/deliveries', { data });
    promise.then(() => this.get('backfills.fetch').perform(), ignorePromiseFailure);
    return promise;
  }

  // "destroyAction" because "destroy" is a reserved internal method name:
  @action
  destroyAction(data) {
    return this._complete('delete', data);
  }

  @action
  retry(data) {
    let deliveryStatus = this.get('deliveryStatus') ?? '';
    data.metric = deliveryStatus;
    return this._complete('retry', data);
  }

  @action
  redeliver(data) {
    return this._complete('redeliver', data);
  }

  @action
  resetDeliveryStatus() {
    this.set('deliveryStatus', null);
  }

  @action
  sendDeliveriesAction(data) {
    return this._complete('send', data);
  }

  @action
  onTimeRangeChange(startTs, endTs, setExportTime = false) {
    if (setExportTime) {
      this.setProperties({ exportStartTs: startTs, exportEndTs: endTs });
    } else {
      this.setProperties({ startTs, endTs });
    }
  }
}
